import { ReactNode, useEffect, useRef, useState } from "react";
import Link from "next/link";
import {
  ArrowDown,
  ChevronDown,
  Info,
  Share2,
  Clapperboard,
} from "lucide-react";
import { AudioPlayer } from "@jammable/ui-components/AudioPlayer";
import { DropdownMenuItem } from "@jammable/ui-components/DropdownMenu";
import { ShareButton } from "@jammable/ui-components/Social";
import { toast } from "@jammable/ui-core";
import { ModelInfo } from "@jammable/clientapi";

import UpgradeModal from "../../../../../../../apps/web/components/modals/UpgradeModal";
import Dropdown from "../../../../../../../apps/web/components/dropdown/Dropdown";
import { Spinner } from "@jammable/ui-components/Loader";
import useAuthModal from "../../../../../../../apps/web/hooks/useAuthModal";
import { useModal } from "../../../../../../../apps/web/context/ModalContext";
import { useAuth } from "../../../../../../../apps/web/context/AuthProvider";
import CreatorUpsellSheet, {
  canUpsellToPower,
} from "../../../../../../../apps/web/components/upsell/CreatorUpsellSheet";
import EarnNudge from "../../../../../../../apps/web/components/referral/EarnNudge";
import { fileSafe, joinArtistNames } from "../../utils";
import { track } from "../../../../../../../apps/web/utils/analytics";
import {
  acapellaNeedsBuilding,
  downloadAcapella,
} from "../../../../../../../apps/web/lib/videostudio/acapella";

interface VoiceTime {
  start: number; // Start time in seconds
  end: number; // End time in seconds
  voice: string; // Corresponding model's `urlParam`
}
export interface ResultProps {
  _id: string;
  outputUrls: {
    audio: string;
    video?: string;
    transcription?: string;
  };
  instrumentalUrl: string;
  convertedUrl: string;
  model: {
    artistname: string;
    urlParam?: string;
  };
  models?: ModelInfo[]; // Adjusting this to match the type in conversionData
  voiceTimes?: VoiceTime[];
  clip?: {
    inTime?: number;
    outTime?: number;
    duration?: number;
  };
  duration?: number;
  isFree: boolean;
  name?: string;
  shortid: string;
  videoSettings?: {
    progress?: number;
    template?: any;
    externalTemplate?: any;
    externalVideoId?: string;
  };
  isUK?: boolean;
  // owner: the lavender player card with the full action row.
  // visitor: a white player inside the shared-link hero, Share + Download.
  variant?: "owner" | "visitor";
  onTimeUpdate?: (seconds: number) => void;
  // "Make a video": the video studio for this cover (/studio/[shortid]).
  videoHref?: string;
  // Rendered at the end of the owner action row (the rate thumbs).
  actionsExtra?: ReactNode;
  // Owner is on Power: their covers come back as WAV. Anyone else gets a
  // one-line "Studio WAV files" upsell at the bottom of the download menu.
  hasPower?: boolean;
}

const pill =
  "tw-inline-flex tw-h-11 tw-cursor-pointer tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border-0 tw-bg-white tw-px-[18px] tw-text-sm tw-font-extrabold tw-text-jam-ink tw-no-underline tw-transition-colors hover:tw-text-jam-purple";

const isWav = (url?: string | null) =>
  !!url && url.split("?")[0].toLowerCase().endsWith(".wav");

const DownloadMenuItem = ({
  children,
  hint,
  onClick,
  tag,
  tagStyle = "ink",
}: {
  children: React.ReactNode;
  hint?: string;
  onClick: () => void;
  // Right-hand pill: "WAV" on lossless files, "POWER" on the upsell row.
  tag?: string;
  tagStyle?: "ink" | "lime";
}) => (
  <DropdownMenuItem
    onClick={onClick}
    className="tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-[18px] tw-px-4 tw-py-2.5 tw-text-jam-ink tw-outline-none hover:tw-bg-jam-lav focus:tw-bg-jam-lav"
  >
    <span className="tw-flex tw-min-w-0 tw-flex-grow tw-flex-col tw-items-start">
      <span className="tw-text-sm tw-font-extrabold">{children}</span>
      {hint && (
        <span className="tw-text-xs tw-font-semibold tw-text-jam-muted">
          {hint}
        </span>
      )}
    </span>
    {tag && (
      <span
        className={`tw-shrink-0 tw-rounded-full tw-px-2.5 tw-py-1 tw-text-[11px] tw-font-extrabold tw-tracking-[0.04em] ${
          tagStyle === "lime"
            ? "tw-bg-jam-lime tw-text-jam-ink"
            : "tw-bg-jam-ink tw-text-jam-lime"
        }`}
      >
        {tag}
      </span>
    )}
  </DropdownMenuItem>
);

export function Result({
  outputUrls,
  instrumentalUrl,
  convertedUrl,
  model,
  models,
  voiceTimes,
  duration,
  clip,
  name,
  isFree,
  shortid,
  videoSettings,
  isUK,
  variant = "owner",
  onTimeUpdate,
  videoHref,
  actionsExtra,
  hasPower = false,
}: ResultProps) {
  const [isDownloading, setIsDownloading] = useState<string | null>(null);
  const [wavUpsell, setWavUpsell] = useState(false);
  const { user } = useAuth();

  // Paywall measurement: plays, downloads and shares weren't counted, so
  // "cover made" was the last step of the funnel anyone could see.
  const coverProps = {
    conversionId: shortid,
    voice: models?.[0]?.urlParam || model?.urlParam || null,
    voices: (models || []).map((m) => m.urlParam).filter(Boolean),
    owner: variant === "owner",
    // Old covers on the retired free allowance: only a clip plays.
    clipOnly: !!isFree,
    source: "conversion-page",
  };
  // Once per play start: the first play, and again after the song has
  // played to the end. Resuming from pause or seeking doesn't count.
  const playStarted = useRef(false);
  useEffect(() => {
    playStarted.current = false;
  }, [shortid]);
  const handlePlay = () => {
    if (playStarted.current) return;
    playStarted.current = true;
    track("Cover Played", coverProps);
  };

  const voiceName = fileSafe(joinArtistNames(model, models));
  const voicePrefix = voiceName ? `${voiceName}-` : "";

  const handleDownload = async (
    url: string | undefined | null,
    type: string,
  ) => {
    if (!url) {
      toast.error("This file isn't ready yet — please refresh.");
      return;
    }

    let status: number | undefined;
    try {
      setIsDownloading(type);
      const extension =
        url.split("?")[0]?.split(".").pop()?.toLowerCase() || "mp3";
      const filename = `${voicePrefix}${type.toUpperCase()}-${name || shortid}.${extension}`;

      const response = await fetch(url);
      status = response.status;
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      link.rel = "noopener";
      // Some mobile browsers ignore .click() on detached anchors, so attach
      // the link to the DOM briefly. The user never sees it.
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(downloadUrl);
      track("Cover Downloaded", { ...coverProps, type });
    } catch (error: any) {
      // Detailed console log so we can grab it from a user's devtools.
      console.error("Download failed:", {
        url,
        type,
        status,
        errorName: error?.name,
        errorMessage: error?.message,
      });

      // Status-specific user message — generic "Download failed" hides the
      // real cause from the user, who then can't tell us anything actionable.
      let userMsg = "Download failed";
      if (status === 404) {
        userMsg = "File not found — please refresh and try again.";
      } else if (status && status >= 500) {
        userMsg = "Server error — please try again in a moment.";
      } else if (
        error?.name === "TypeError" ||
        error?.message === "Failed to fetch"
      ) {
        userMsg = "Network error — check your connection.";
      }
      toast.error(userMsg);

      // Fire-and-forget telemetry so we can aggregate failures by URL/host/
      // status and stop guessing. keepalive lets the POST survive the user
      // navigating away after the toast.
      try {
        fetch("/api/log/download-failure", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            url,
            type,
            status,
            message: error?.message,
            name: error?.name,
            conversionShortId: shortid,
          }),
          keepalive: true,
        }).catch(() => {});
      } catch {
        /* never let telemetry mask the real error */
      }
    } finally {
      setIsDownloading(null);
    }
  };

  const artistNames = joinArtistNames(model, models);

  const { checkAuthAndExecute } = useAuthModal();
  const { openModal } = useModal();

  const videoReady = Boolean(
    outputUrls?.video && videoSettings?.progress === 100,
  );
  const hasVideoTemplate = Boolean(
    videoSettings?.template || videoSettings?.externalTemplate,
  );

  // Old covers made on the retired free allowance only unlocked a clip, so
  // their download still routes to plans instead of the files.
  const downloadButton = isFree ? (
    <button
      className={pill}
      onClick={() => {
        if (!checkAuthAndExecute()) return false;
        openModal(
          "upgrade-modal",
          <UpgradeModal from="conversion-page-download" dailyLimit={false} />,
          { className: "tw-p-0" },
        );
      }}
    >
      <ArrowDown size={16} />
      Download
    </button>
  ) : (
    <Dropdown
      className="tw-w-72 tw-rounded-[26px] tw-border-2 tw-border-jam-line tw-p-2"
      dropdownItems={
        <>
          {outputUrls?.audio && (
            <DownloadMenuItem
              hint="Vocals and music together"
              tag={isWav(outputUrls?.audio) ? "WAV" : undefined}
              onClick={() => handleDownload(outputUrls?.audio, "combined")}
            >
              The cover
            </DownloadMenuItem>
          )}
          {convertedUrl && (
            <DownloadMenuItem
              hint="Just the new voice"
              tag={isWav(convertedUrl) ? "WAV" : undefined}
              onClick={() => {
                // Several voices: convertedUrl is one voice singing the
                // whole song. Build the real acapella from each voice's
                // track instead (apps/web/lib/videostudio/acapella).
                const cover = { models, voiceTimes, convertedUrl };
                if (!acapellaNeedsBuilding(cover)) {
                  handleDownload(convertedUrl, "acapella");
                  return;
                }
                setIsDownloading("acapella");
                downloadAcapella(
                  cover,
                  `${voicePrefix}${name || shortid || "jammable"}`,
                )
                  .then(() => {
                    // Not returned: the spinner mustn't wait on Mixpanel.
                    track("Cover Downloaded", {
                      ...coverProps,
                      type: "acapella",
                    });
                  })
                  .catch(() =>
                    toast.error(
                      "Couldn't make the vocals file. Please try again.",
                    ),
                  )
                  .finally(() => setIsDownloading(null));
              }}
            >
              Vocals only
            </DownloadMenuItem>
          )}
          {instrumentalUrl && (
            <DownloadMenuItem
              hint="The track without vocals"
              tag={isWav(instrumentalUrl) ? "WAV" : undefined}
              onClick={() => handleDownload(instrumentalUrl, "instrumental")}
            >
              Music only
            </DownloadMenuItem>
          )}
          {videoReady && (
            <DownloadMenuItem
              hint="Vertical, for TikTok and Reels"
              onClick={() => handleDownload(outputUrls?.video, "video")}
            >
              Video
            </DownloadMenuItem>
          )}
          {variant === "owner" && !hasPower && (
            <DownloadMenuItem
              hint="Lossless on Studio"
              tag="POWER"
              tagStyle="lime"
              onClick={() =>
                canUpsellToPower(user)
                  ? setWavUpsell(true)
                  : openModal(
                      "upgrade-modal",
                      <UpgradeModal
                        from="conversion-page-wav"
                        dailyLimit={false}
                        plan="power"
                      />,
                      { className: "tw-p-0" },
                    )
              }
            >
              Studio WAV files
            </DownloadMenuItem>
          )}
          <div className="tw-mt-1 tw-flex tw-items-start tw-gap-2 tw-rounded-[18px] tw-bg-jam-lav tw-p-3">
            <Info
              size={16}
              className="tw-mt-0.5 tw-shrink-0 tw-text-jam-purple"
            />
            <div>
              <div className="tw-text-sm tw-font-extrabold tw-text-jam-ink">
                Sharing to social media?
              </div>
              <div className="tw-text-xs tw-font-semibold tw-text-jam-muted">
                Include <span className="tw-font-extrabold">#jammable</span> in
                your posts for a chance to be a partnered creator!
              </div>
            </div>
          </div>
        </>
      }
    >
      <button className={pill} aria-label="Download">
        {isDownloading ? (
          <Spinner className="tw-size-4 tw-border-3" />
        ) : (
          <ArrowDown size={16} />
        )}
        Download
        <ChevronDown size={16} className="tw-text-jam-muted" />
      </button>
    </Dropdown>
  );

  const shareButton = (
    <ShareButton
      title={`Check out my AI ${artistNames} cover on Jammable!`}
      onShare={(target) => track("Cover Shared", { ...coverProps, target })}
    >
      <button className={pill}>
        <Share2 size={16} />
        Share
      </button>
    </ShareButton>
  );

  // Video picked before the cover was made: show it (or its progress) next
  // to the player.
  const videoPanel = hasVideoTemplate ? (
    <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-[220px] tw-shrink-0 tw-flex-col tw-items-center tw-gap-2 sm:tw-mx-0 sm:tw-w-[180px]">
      {videoReady ? (
        <>
          <video
            controls
            className="tw-w-full tw-rounded-[22px] tw-bg-jam-ink tw-object-cover"
            src={outputUrls.video}
            style={{ aspectRatio: "9/16" }}
          />
          <button
            onClick={async () => {
              if (!outputUrls?.video) return;
              handleDownload(outputUrls.video, "video");
            }}
            className={pill}
            aria-label="Download video"
          >
            {isDownloading === "video" ? (
              <Spinner className="tw-size-4 tw-border-3" />
            ) : (
              <ArrowDown size={16} />
            )}
            Video
          </button>
        </>
      ) : (
        <div
          className="tw-flex tw-w-full tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-rounded-[22px] tw-bg-jam-ink tw-p-3 tw-text-center tw-text-white"
          style={{ aspectRatio: "9/16" }}
        >
          <span className="tw-text-[13px] tw-font-extrabold">
            Making your video
          </span>
          <span className="jam-display tw-text-3xl tw-font-extrabold tw-text-jam-lime">
            {Math.round(videoSettings?.progress ?? 0)}%
          </span>
        </div>
      )}
    </div>
  ) : null;

  const player = (
    <div className="tw-w-full tw-min-w-0 tw-rounded-[26px] tw-bg-white tw-p-3 md:tw-p-4">
      <AudioPlayer
        url={outputUrls?.audio || convertedUrl}
        models={models}
        voiceTimes={voiceTimes}
        duration={duration}
        clip={isFree ? clip : undefined}
        onTimeUpdate={onTimeUpdate}
        onPlay={handlePlay}
        onEnded={() => {
          playStarted.current = false;
        }}
        className="tw-bg-transparent tw-p-0 tw-text-jam-purple"
      />
    </div>
  );

  if (variant === "visitor") {
    return (
      <div className="tw-flex tw-flex-col tw-gap-3">
        <div className="tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row">
          {videoPanel}
          {player}
        </div>
        {!isUK && (
          <div className="tw-flex tw-flex-wrap tw-gap-2">
            {shareButton}
            {downloadButton}
          </div>
        )}
      </div>
    );
  }

  return (
    <section className="tw-flex tw-flex-col tw-gap-4 tw-rounded-[30px] tw-bg-jam-lav tw-p-4 md:tw-rounded-[36px] md:tw-p-6">
      <div className="tw-flex tw-flex-col tw-gap-4 sm:tw-flex-row">
        {videoPanel}
        {player}
      </div>
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
        {!isUK && (
          <>
            {shareButton}
            {downloadButton}
          </>
        )}
        {videoHref && (
          <Link href={videoHref} className={pill}>
            <Clapperboard size={16} />
            Make a video
          </Link>
        )}
        {actionsExtra && (
          <>
            <span className="tw-hidden tw-flex-grow md:tw-block" />
            {actionsExtra}
          </>
        )}
      </div>
      {/* Where sharing is offered, so is getting paid for it. */}
      {!isUK && <EarnNudge where="cover" />}
      <CreatorUpsellSheet
        plan="power"
        open={wavUpsell}
        reason="wav"
        onClose={() => setWavUpsell(false)}
        onUpgraded={() => {
          setWavUpsell(false);
          toast.success("You're on Studio. Your next covers come back as WAV.");
        }}
      />
    </section>
  );
}
